import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { chatLogger } from "@/utils/logger";

export type WebSocketStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

interface WebSocketContextType {
  status: WebSocketStatus;
  subscribe: (topic: string, handler: (msg: any) => void) => () => void;
  sendMessage: (destination: string, body: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const clientRef = useRef<Client | null>(null);
  const [status, setStatus] = useState<WebSocketStatus>("disconnected");
  const subscriptions = useRef<Map<string, StompSubscription>>(new Map());
  const handlers = useRef<Map<string, (msg: any) => void>>(new Map());

  useEffect(() => {
    chatLogger.log("WebSocketProvider MONTADO");
    setStatus("connecting");
    const API_BASE_URL =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:2222/api";
    const WS_BASE_URL = API_BASE_URL.replace(/\/api$/, "");
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE_URL}/ws-chat`),
      reconnectDelay: 3000,
      onConnect: () => {
        chatLogger.log("WebSocket CONNECTED");
        setStatus("connected");
        // Reinscrever em todos os tópicos ativos
        subscriptions.current.forEach((sub, topic) => {
          sub.unsubscribe();
          const handler = handlers.current.get(topic);
          if (handler) {
            const newSub = client.subscribe(topic, (msg: IMessage) => {
              handler(JSON.parse(msg.body));
            });
            subscriptions.current.set(topic, newSub);
          }
        });
      },
      onDisconnect: () => {
        setStatus("disconnected");
        chatLogger.log("WebSocket DISCONNECTED");
      },
      onStompError: () => {
        setStatus("reconnecting");
        chatLogger.log("WebSocket STOMP ERROR");
      },
      onWebSocketClose: () => {
        setStatus("reconnecting");
        chatLogger.log("WebSocket CLOSED");
      },
      debug: () => {},
    });
    client.activate();
    clientRef.current = client;
    return () => {
      chatLogger.log("WebSocketProvider DESTRUIDO");
      subscriptions.current.forEach((sub) => sub.unsubscribe());
      client.deactivate();
    };
  }, []);

  // Assinar tópico dinamicamente
  const subscribe = useCallback(
    (topic: string, handler: (msg: any) => void) => {
      if (!clientRef.current || !clientRef.current.connected) return () => {};
      // Se já está inscrito, desinscreve
      if (subscriptions.current.has(topic)) {
        subscriptions.current.get(topic)?.unsubscribe();
      }
      handlers.current.set(topic, handler);
      const sub = clientRef.current.subscribe(topic, (msg: IMessage) => {
        handler(JSON.parse(msg.body));
      });
      subscriptions.current.set(topic, sub);
      // Função de cleanup
      return () => {
        sub.unsubscribe();
        subscriptions.current.delete(topic);
        handlers.current.delete(topic);
      };
    },
    []
  );

  // Enviar mensagem para destino
  const sendMessage = useCallback((destination: string, body: any) => {
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.publish({ destination, body: JSON.stringify(body) });
    }
  }, []);

  return (
    <WebSocketContext.Provider value={{ status, subscribe, sendMessage }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export function useWebSocket() {
  const ctx = useContext(WebSocketContext);
  if (!ctx)
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  return ctx;
}
